import { Link } from "react-router-dom";
import { HeartHandshake, Feather, CloudSun, Sprout, ArrowRight } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { IMAGES } from "@/data/site";

const VALUES = [
  {
    icon: HeartHandshake,
    tone: "bg-lav-soft text-lav-deep",
    title: "Warm, not clinical",
    body: "Approachable pages and plain language—no medical jargon, no intimidating forms, no gatekeeping.",
  },
  {
    icon: Feather,
    tone: "bg-mint-soft text-mint-deep",
    title: "Simple by design",
    body: "Big, clear prompts that take minutes. If you can write a note to a friend, you can use this journal.",
  },
  {
    icon: CloudSun,
    tone: "bg-blush-soft text-blush-deep",
    title: "Room for real healthcare",
    body: "Multiple providers, changing medications, referrals, follow-ups—built for how healthcare really works.",
  },
  {
    icon: Sprout,
    tone: "bg-lav-soft text-lav-deep",
    title: "Complements your care",
    body: "Works alongside MyChart, patient portals, and medical records—never replaces your doctor's advice.",
  },
];

export default function About() {
  return (
    <main data-testid="page-about" className="pt-32 sm:pt-40 lg:pt-44">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>Our story</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="mt-7 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl"
              data-testid="about-headline"
            >
              Your healthcare, finally in <em className="italic text-lav-deep">one place</em>.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <div className="space-y-6 text-base leading-relaxed text-ink-soft sm:text-lg">
            <Reveal>
              <p data-testid="about-story-1">
                In Your Corner Healthcare was created by a nurse and healthcare advocate who saw the same thing
                again and again: patients trying to manage appointments, medications, symptoms, questions,
                multiple healthcare providers, referrals, instructions, and follow-up steps—across too many
                different places.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p data-testid="about-story-2">
                Some patients use technology comfortably. Others prefer paper. Many use patient portals but still
                want something they can physically bring to an appointment, write in, and take home.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p data-testid="about-story-3">
                The purpose is simple: give patients one place to organize the healthcare information that
                matters, so they can better understand their care and know what comes next.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="border-l-2 border-blush pl-5 font-display text-xl italic text-ink" data-testid="about-story-quote">
                "Because you deserve to understand your own healthcare."
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-t-[10rem] rounded-b-3xl shadow-xl shadow-lav-soft/60">
              <img
                src={IMAGES.windowLight}
                alt="A quiet moment of reflection by the window"
                className="aspect-[4/5] w-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="about-values-headline">
              What we stand for
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06} className="h-full">
                <article
                  data-testid={`about-value-${i + 1}`}
                  className="h-full rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-lav-soft/70"
                >
                  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${v.tone}`}>
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-ink">{v.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-ink-soft">{v.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-lav-deep px-6 py-14 text-center sm:px-12 lg:py-20">
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blush/25 blur-3xl" />
              <div className="relative">
                <h2 className="mx-auto max-w-2xl font-display text-3xl font-semibold text-white sm:text-4xl" data-testid="about-cta-headline">
                  This was made for people like you.
                </h2>
                <Link
                  to="/shop"
                  data-testid="about-cta-shop"
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-bold text-ink transition-all duration-300 hover:bg-blush-soft hover:text-blush-deep"
                >
                  Meet the Journal
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
